import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { invitationToken } from '../../core/http/invitation-link';
import { getApiUrl } from '../../core/http/http-client';
import { GameService } from '../../core/state/game.service';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { Language } from '../../core/i18n/translations';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, IconComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent {
  readonly game = inject(GameService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly isSettingsMenuOpen = signal<boolean>(false);
  readonly googleLoginEnabled = environment.features.googleLogin;
  readonly isGoogleLoading = signal<boolean>(false);

  readonly unverifiedEmail = signal<string | null>(null);
  readonly isResending = signal<boolean>(false);
  readonly resendCooldown = signal<number>(0);
  readonly resendSuccess = signal<boolean>(false);
  private cooldownTimer: any = null;

  toggleSettingsMenu(): void {
    this.isSettingsMenuOpen.update((v) => !v);
  }

  closeSettingsMenu(): void {
    this.isSettingsMenuOpen.set(false);
  }

  selectLanguage(lang: Language): void {
    this.game.i18n.setLanguage(lang);
  }

  get invitationFragment(): string | undefined {
    return this.route.snapshot.fragment || undefined;
  }

  readonly loginForm = this.fb.group({
    user: ['', [Validators.required]],
    password: ['', [Validators.required]],
    rememberMe: [true],
  });

  handleGoogleLogin(): void {
    if (this.isGoogleLoading()) return;
    const fragment = this.route.snapshot.fragment || '';
    const token = invitationToken(`/leagues#${fragment}`);
    if (token && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('lom_pending_invitation', token);
    }
    this.isGoogleLoading.set(true);
    window.location.href = getApiUrl('/api/auth/social/google/start');
  }

  async handleResendConfirmation(): Promise<void> {
    const email = this.unverifiedEmail();
    if (!email || this.resendCooldown() > 0 || this.isResending()) return;

    this.isResending.set(true);
    this.resendSuccess.set(false);
    try {
      await this.game.resendRegistration({ email });
      this.resendSuccess.set(true);
      this.startResendCooldown(60);
    } catch (err: any) {
      if (err?.status === 429) {
        const cd = err.retryAfter || 60;
        this.startResendCooldown(cd);
      }
    } finally {
      this.isResending.set(false);
    }
  }

  private startResendCooldown(seconds: number): void {
    this.resendCooldown.set(seconds);
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
    this.cooldownTimer = setInterval(() => {
      this.resendCooldown.update((v) => {
        if (v <= 1) {
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
          return 0;
        }
        return v - 1;
      });
    }, 1000);
  }

  async handleSubmit() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    const { user, password } = this.loginForm.getRawValue();
    if (user && password) {
      this.unverifiedEmail?.set(null);
      this.resendSuccess?.set(false);
      try {
        await this.game.login(user.trim(), password);
        const fragment = this.route.snapshot.fragment || '';
        const token = invitationToken(`/leagues#${fragment}`);
        this.router.navigate([token ? '/leagues' : '/dashboard'], { fragment: token ? `invitation=${token}` : undefined });
      } catch (err: any) {
        if (err?.code === 'email_not_verified' || err?.responseBody?.code === 'email_not_verified' || (err?.status === 403 && err?.message?.toLowerCase().includes('verifi'))) {
          this.unverifiedEmail?.set(user.trim());
          return;
        }
      }
    }
  }
}
